export type AssetType =
  | 'IMAGE_ASSET'
  | 'HTML5_ASSET'
  | 'CTV_ASSET'
  | 'VIDEO_ASSET'
  | 'AUDIO_ASSET'
  | 'UNSET';

export interface DimensionFilter {
  heightInPxEquals: number;
  widthInPxEquals: number;
}

export interface AspectRatioFilter {
  aspectHeight: number;
  aspectWidth: number;
}

export interface GetAssetsByIdRequest {
  assetIds: string[];
}

export interface GetAssetsByIdResponse {
  assets: V1Asset[];
}

export interface SearchLibraryAssetsRequest {
  assetTypeAnyOf: (AssetType | 'UNSET')[];
  assetNameContains?: string;
  dimensionsAreAnyOf?: { filters: DimensionFilter[] };
  aspectRatioIs?: AspectRatioFilter;
  minimumDimensions?: DimensionFilter;
  maximumDimensions?: DimensionFilter;
  sortDirection?:
    | 'LAST_CREATIVE_CREATED_DESC'
    | 'LAST_CREATIVE_CREATED_ASC'
    | 'CREATED_AT_DESC'
    | 'CREATED_AT_ASC';
}

export interface SearchLibraryAssetsResponse {
  records: V1Asset[];
  nextPageToken: string;
}

export interface CreativeGroup {
  id: string;
  name: string;
  creativeIds: string[];
  errored: boolean;
}

export interface APICreative {
  id: string;
  assetId: string;
  landingPageUrl: string;
  clickThroughUrl: string;
  highRiskClickThroughUrl: string;
  createdAt: string;
  updatedAt: string;
  asset: V1Asset;
  configureLandingPageWithDefaultUtmParameters: boolean;
  CALErrors: string[];
  ttdConfig: CreativeProviderConfig;
}

export interface V1Asset {
  id: string;
  fileName: string;
  mimeType: string;
  assetType: AssetType;
  tags: Record<string, string>;
  version?: V1AssetVersion;
  versions?: V1AssetVersion[];
  ready?: boolean;
}

export function V1AssetFactory(
  id = '',
  overrides: Partial<V1Asset> = {},
): V1Asset {
  return {
    id,
    version: V1AssetVersionFactory(),
    assetType: 'UNSET',
    fileName: '',
    mimeType: '',
    tags: {},
    ...overrides,
  };
}

export function V1AssetVersionFactory(
  id = '',
  overrides: Partial<V1AssetVersion> = {},
): V1AssetVersion {
  return {
    id,
    uploadedSizeBytes: 0,
    uploadedAt: new Date(2020, 10, 10).toISOString(),
    errored: false,
    approvedForAdvertisingUse: true,
    customerFacingStatusMessage: '',
    defaultVersion: true,
    ...DisplayInformationFactory(),
    ...overrides,
  };
}

export interface CreativeAssetParams {
  assetId: string;
  landingPageUrl: string;
  clickThroughUrl: string;
  configureLandingPageWithDefaultUtmParameters: boolean;
  ttdConfig: CreativeProviderConfig;
}

export interface CreativeProviderConfig {
  thirdPartyImpressionTrackingUrl: string;
  thirdPartyImpressionTrackingUrl2: string;
  thirdPartyImpressionTrackingUrl3: string;
  thirdPartyTrackingTag?: string;
}

export interface UpdateCreativeParams {
  creativeId: string;
  assetId: string;
  landingPageUrl: string;
  clickThroughUrl: string;
  configureLandingPageWithDefaultUtmParameters: boolean;
  ttdConfig: CreativeProviderConfig;
}

export interface CreateCreativeGroupParams {
  name: string;
  creativeIds: string[];
}

export interface UpdateCreativeGroupParams {
  creativeGroupId: string;
  name: string;
  creativeIds: string[];
}

export function getAssetType(mimeType: string): AssetType {
  const creativeTypesMap: Record<string, AssetType> = {
    'image/png': 'IMAGE_ASSET',
    'image/jpg': 'IMAGE_ASSET',
    'image/jpeg': 'IMAGE_ASSET',
    'image/gif': 'IMAGE_ASSET',
    'video/mp4': 'VIDEO_ASSET',
    'application/zip': 'HTML5_ASSET',
    'application/x-zip-compressed': 'HTML5_ASSET',
    'audio/mpeg': 'AUDIO_ASSET',
    'audio/wav': 'AUDIO_ASSET',
    'audio/m4a': 'AUDIO_ASSET',
  };

  return creativeTypesMap[mimeType] || 'UNSET';
}

export type CreativeCreativeGroupResponse = CreativeGroup;

export type CreativeCreativeResponse = APICreative;
export type UpdateCreativeResponse = APICreative;

export type GetCreativeGroupsByIdResponse = { creativeGroups: CreativeGroup[] };
export type GetCreativesByIdResponse = { creatives: APICreative[] };

export interface AddAssetToLibraryRequest {
  slermsFileUuid: string;
  fileName: string;
  mimeType: string;
  assetType: AssetType;
  tags: Record<string, string>;
  expectedFileSizeInBytes: number;
  ownerIds?: Record<string, string>;
}

export interface AddAssetToLibraryResponse {
  assetId: string;
}

export interface V1AssetVersion {
  id: string;
  uploadedSizeBytes: number;
  uploadedAt: string;
  errored: boolean;
  approvedForAdvertisingUse: boolean;
  customerFacingStatusMessage: string;
  defaultVersion?: boolean;
  md5?: string;
  imageDisplay?: ImageDisplay;
  videoDisplay?: VideoDisplay;
  html5Display?: Html5Display;
  audioDisplay?: AudioDisplay;
}

export interface ValidateCreativeRequest {
  creativeId: string;
  destination: 'TTD';
}

export interface ValidateCreativeResponse {
  errorMessages: string[];
  valid: boolean;
}

export interface UpdateAssociatedAssetRequest {
  assetId: string;
  associatedAssetId: string;
}

export function DisplayInformationFactory(
  overrides: Partial<DisplayInformation> = {},
): DisplayInformation {
  return {
    imageDisplay: imageDisplayFactory() || {},
    videoDisplay: videoDisplayFactory(),
    html5Display: html5DisplayFactory(),
    audioDisplay: audioDisplayFactory(),
    ...overrides,
  };
}

export function imageDisplayFactory(
  src = '',
  widthPx = 0,
  heightPx = 0,
  overrides: Partial<ImageDisplay> = {},
): ImageDisplay {
  return {
    customerBilledSrc: src,
    imageSrc: src,
    widthPx,
    heightPx,
    ...overrides,
  };
}

export function videoDisplayFactory(
  src = '',
  widthPx = 0,
  heightPx = 0,
  overrides: Partial<VideoDisplay> = {},
): VideoDisplay {
  return {
    customerBilledSrc: src,
    videoSrc: src,
    widthPx,
    heightPx,
    ...overrides,
  };
}

export function html5DisplayFactory(
  src = '',
  widthPx = 0,
  heightPx = 0,
  overrides: Partial<Html5Display> = {},
): Html5Display {
  return {
    customerBilledIframeSrc: src,
    iframeSrc: src,
    originalProvidedFile: src,
    widthPx,
    heightPx,
    previewSrc: src,
    ...overrides,
  };
}

export function audioDisplayFactory(
  src = '',
  dimensions: Dimensions = { width: 0, height: 0 },
  companionSrcUrl = '',
  companionImage = { imageSrc: '' },
  overrides: Partial<Html5Display> = {},
): AudioDisplay {
  return {
    audioSrc: src,
    dimensions,
    companionSrcUrl,
    companionImage,
    ...overrides,
  };
}

export interface DisplayInformationProp {
  prop: string;
  factory: (
    src: string,
    dimensions: Dimensions,
    companionSrcUrl?: string,
  ) => ImageDisplay | VideoDisplay | Html5Display | AudioDisplay;
}

export interface Dimensions {
  width: number;
  height: number;
}

export interface ImageDisplay {
  customerBilledSrc: string;
  imageSrc: string;
  widthPx: number;
  heightPx: number;
}

export interface Html5Display {
  customerBilledIframeSrc: string;
  iframeSrc: string;
  originalProvidedFile: string;
  widthPx: number;
  heightPx: number;
  previewSrc: string;
}

export interface VideoDisplay {
  customerBilledSrc: string;
  videoSrc: string;
  widthPx: number;
  heightPx: number;
}

export interface AudioDisplay {
  audioSrc: string;
  dimensions: Dimensions;
  companionSrcUrl: string;
  companionImage: {
    imageSrc: string;
  };
}

export interface DisplayInformation {
  imageDisplay?: ImageDisplay;
  videoDisplay?: VideoDisplay;
  html5Display?: Html5Display;
  audioDisplay?: AudioDisplay;
}

export function getDisplayInformationPropAndFactory(
  type: AssetType,
): DisplayInformationProp {
  const displayInfoProps: Record<AssetType, DisplayInformationProp> = {
    ['IMAGE_ASSET']: {
      prop: 'imageDisplay',
      factory: (src: string, dimensions: Dimensions) =>
        imageDisplayFactory(src, dimensions.width, dimensions.height),
    },
    ['CTV_ASSET']: {
      prop: 'videoDisplay',
      factory: (src: string, dimensions: Dimensions) =>
        videoDisplayFactory(src, dimensions.width, dimensions.height),
    },
    ['VIDEO_ASSET']: {
      prop: 'videoDisplay',
      factory: (src: string, dimensions: Dimensions) =>
        videoDisplayFactory(src, dimensions.width, dimensions.height),
    },
    ['HTML5_ASSET']: {
      prop: 'html5Display',
      factory: (src: string, dimensions: Dimensions) =>
        html5DisplayFactory(src, dimensions.width, dimensions.height),
    },
    ['AUDIO_ASSET']: {
      prop: 'audioDisplay',
      factory: (
        src: string,
        dimensions: Dimensions,
        companionSrcUrl: string = '',
      ) => audioDisplayFactory(src, dimensions, companionSrcUrl),
    },
    ['UNSET']: {
      prop: 'imageDisplay',
      factory: (src: string, dimensions: Dimensions) =>
        imageDisplayFactory(src, dimensions.width, dimensions.height),
    },
  };

  return displayInfoProps[type];
}

export interface SetAssetLibraryVisibilityRequest {
  assetId: string;
  visibleInLibrary: boolean;
}
